import { ChangeDetectionStrategy, Component, Type, effect, inject, signal, untracked } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import * as Sentry from '@sentry/angular';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { NOTIFIER } from '@shared/notifier/types';
import { SupportAccessService } from './support-access.service';
import { SupportBadgeService } from './support-badge.service';
import { SupportContextService } from './support-context.service';
import { SupportPanelService, SupportTab } from './support-panel.service';

type SupportPanelContentModule = { SupportPanelContentComponent: Type<unknown> };

/**
 * App-brede schil van het support-paneel: handle met badge, tabs en de inhoud
 * per tab. Wordt door de layout enkel gerenderd als de gebruiker de
 * support-feature heeft. De inhoud komt via een dynamische import zodra het
 * paneel voor het eerst opengaat: tot dan bestaat er alleen de handle en een
 * lege schil.
 */
@Component({
  selector: 'support-panel',
  imports: [SharedModule, TranslocoModule, NgComponentOutlet],
  templateUrl: './support-panel.component.html',
  styleUrl: './support-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportPanelComponent {
  protected readonly panel = inject(SupportPanelService);
  protected readonly access = inject(SupportAccessService);
  protected readonly badge = inject(SupportBadgeService);
  private readonly context = inject(SupportContextService);
  private readonly notifier = inject(NOTIFIER);
  private readonly transloco = inject(TranslocoService);

  protected readonly content = signal<Type<unknown> | undefined>(undefined);

  /** Overschrijfbaar in specs; de echte import levert de lazy chunk. */
  protected loadContent: () => Promise<SupportPanelContentModule> = () => import('./support-panel-content.component');

  private loadStarted = false;

  /**
   * Sentry Replay draait meestal in bufferstand (alleen de laatste minuut in het geheugen).
   * Eén flush bij de eerste opening van het paneel verstuurt die minuut en zet de opname
   * door als sessieopname, zodat de aanloop naar een melding in de replay zit. Overschrijfbaar
   * in specs; Sentry kan uit staan, dan is getReplay() undefined.
   */
  protected flushReplay: () => unknown = () => Sentry.getReplay()?.flush();
  private replayFlushed = false;

  constructor() {
    this.panel.consumeDeepLink();

    effect(() => {
      if (!this.panel.open() || this.loadStarted) return;
      this.loadStarted = true;
      untracked(() => void this.ensureContent());
    });

    // Bij elke overgang naar open, ook via deep link of openAt(): de pagina
    // waarop het paneel openging hoort bij de context van de melding.
    let wasOpen = false;
    effect(() => {
      const open = this.panel.open();
      if (open && !wasOpen) {
        untracked(() => {
          this.context.snapshotOnOpen();
          this.flushReplayOnce();
        });
      }
      wasOpen = open;
    });
  }

  private flushReplayOnce(): void {
    if (this.replayFlushed) return;
    this.replayFlushed = true;
    try {
      Promise.resolve(this.flushReplay()).catch(() => undefined);
    } catch {
      // Sentry uit of niet geïnitialiseerd: de melding zelf hangt hier niet van af.
    }
  }

  protected toggle(): void {
    const opening = !this.panel.open();
    this.panel.toggle();
    if (!opening) return;
    void this.badge.refresh();
  }

  protected selectTab(tab: SupportTab): void {
    this.panel.activeTab.set(tab);
  }

  private async ensureContent(): Promise<void> {
    try {
      const module = await this.loadContent();
      this.content.set(module.SupportPanelContentComponent);
    } catch {
      this.loadStarted = false;
      this.notifier.error(this.transloco.translate<string>('support.panel.loadFailed'));
    }
  }
}
